import { css } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { FontStyle } from '@inter-ikea-kompis/themes';

import InputLayoutEnum from '../../enums/InputLayoutEnum.js';
import InputTypeEnum from '../../enums/InputTypeEnum.js';
import type IStyleOptions from '../../types/IStyleOptions.js';

const LINE_HEIGHT = 26;

export default class InputStyle {
	/**
	 * @param options The props.
	 * @param options.diabled If it is disabled.
	 * @param options.hasValue If it has value.
	 * @param options.hasFocus If it has focus.
	 * @param options.hasLabel If it has label.
	 * @param options.hasLeadingIcon If it has a leading icon.
	 * @param options.hasTrailingIcon If it has a trailing icon.
	 * @param options.theme Theme.
	 * @param options.disabled ...
	 * @param options.type Type.
	 * @returns Styles.
	 */
	public static getStyle(options: IStyleOptions): string {
		const { color, spacing, motion } = options.theme;

		return css`
			${FontStyle.getStyle({
				type: ThemeFontStyleTypeEnum.bodyLarge,
				theme: options.theme
			})}
			label,
			input {
				outline: none;
				width: auto;
				margin: 0;
				padding: 0;
				font-size: inherit;
				font-family: inherit;
				-webkit-appearance: none;
			}
			/* remove default arrows in input type=number */
			input::-webkit-outer-spin-button,
			input::-webkit-inner-spin-button {
				-webkit-appearance: none;
				margin: 0; /* <-- Apparently some margin are still there even though it's hidden */
			}
			input[type='number'] {
				/* remove default arrows in input type=number in Firefox*/
				-moz-appearance: textfield;
			}
			input:disabled {
				background-color: transparent;
			}
			:host {
				display: flex;
				flex-direction: column;
				width: auto;
			}

			${this.getRtl(options)}

			.prefix,
			.suffix {
				${this.getPadding(options)}
				align-self: center;
				white-space: nowrap;
				opacity: ${options.hasFocus || options.hasValue ? 1 : 0};
				transition-property: opacity;
				color: ${options.disabled ? color.disabled.default : color.text.lightest};
				transition-duration: ${motion.duration.small};
				transition-delay: ${options.hasFocus ? motion.delay.medium : '0s'};
			}

			kompis-icon {
				${this.getPadding(options)}
				align-self: center;
				color: ${options.disabled ? color.disabled.default : color.icon.default};
			}

			.container {
				position: relative;
				display: flex;
				flex-direction: column;
			}

			.input {
				pointer-events: ${options.layout === InputLayoutEnum.readOnly ? 'none' : 'auto'};
				position: relative;
				display: flex;
				width: 100%;
				${options.type === InputTypeEnum.default
					? `border-bottom: ${1 / 16}rem solid ${this.getBorderColor(options)};`
					: ''}
				transition-property: border, box-shadow;
				transition-duration: ${motion.duration.small};
				transition-timing-function: ${motion.ease.easy};
				box-shadow: ${this.getBoxShadow(options)};
			}

			input {
				${this.getPadding(options)}
				width: 100%;
				flex-grow: 1;
				min-width: 0;
				line-height: ${LINE_HEIGHT / 16}rem;
				border: none;
				color: ${options.disabled ? color.disabled.default : color.text.dark};
			}

			input::-ms-clear,
			input::-ms-reveal,
			input::-webkit-search-decoration,
			input::-webkit-search-cancel-button,
			input::-webkit-search-results-button,
			input::-webkit-search-results-decoration {
				display: none;
				width: 0;
				height: 0;
			}

			.helpContainer {
				display: flex;
			}

			.helpMessage {
				color: ${this.getHelpMessageColor(options)};
				display: flex;
				flex-grow: 1;
				padding-top: ${spacing.space25 / 16}rem;
				align-items: flex-start;
			}

			.characterCount {
				flex-shrink: 0;
				color: ${this.getCharacterCountColor(options)};
			}

			.labelContainer {
				position: absolute;
				display: flex;
				width: 100%;
				align-items: flex-end;
				top: 0;
				right: 0;
				left: 0;
				bottom: 0;
				pointer-events: none;
			}
			label {
				overflow: hidden;
				pointer-events: none;
				line-height: ${LINE_HEIGHT / 16}rem;
				${options.hasFocus || options.hasValue ? '' : this.getPadding(options)}
				transition-property: transform, color, font-size, line-height, padding;
				transition-duration: ${motion.duration.small};
				transition-timing-function: ${motion.ease.easy};
				color: ${options.disabled ? color.disabled.default : color.text.default};
				transform: ${this.getLabelTransform(options)};
			}

			.divider {
				flex-shrink: 0;
				width: ${spacing.space25 / 16}rem;
			}

			${this.getRtlLabel(options)}
			${this.getLabelFontStyle(options)}
			${this.getHover(options)}
		`;
	}

	public static getHover(options: IStyleOptions): string {
		if (options.type === InputTypeEnum.subtle) {
			return css`
				.input:hover {
					box-shadow: 0 ${(options.hasFocus ? 2 : 1) / 16}rem 0 0 ${this.getBorderColor(options)};
				}
			`;
		}

		return '';
	}

	/**
	 * Get rtl styling.
	 *
	 * @param options Options.
	 */
	public static getRtl(options: IStyleOptions): string {
		if (options.direction === 'rtl') {
			return css`
				:host input[inputMode='tel'] {
					direction: ltr;
					unicode-bidi: embed;
					text-align: end;
				}
				:host-context input[inputMode='tel'] {
					direction: ltr;
					unicode-bidi: embed;
					text-align: end;
				}
			`;
		}
		return css`
			:host(:dir(rtl)) input[inputMode='tel'] {
				direction: ltr;
				unicode-bidi: embed;
				text-align: end;
			}
			:host-context([dir='rtl']) input[inputMode='tel'] {
				direction: ltr;
				unicode-bidi: embed;
				text-align: end;
			}
		`;
	}

	/**
	 *
	 * @param options ...
	 */
	public static getCharacterCountColor(options: IStyleOptions): string {
		const { color } = options.theme;
		if (options.disabled) {
			return color.disabled.default;
		} else if (options.characterCountExceeded) {
			return color.semantic.negative;
		}
		return color.text.lightest;
	}

	/**
	 * Padding for input.
	 *
	 * @param options Options.
	 * @returns Padding style.
	 */
	public static getPadding(options: IStyleOptions): string {
		const label = options.hasLabel;
		const { spacing } = options.theme;

		return css`
			padding-top: ${label ? spacing.space125 / 16 : 0}rem;
			padding-bottom: ${spacing.space25 / 16}rem;
		`;
	}

	/**
	 * @param options Options.
	 * @param options.hasLabel If it has label.
	 * @param options.hasLeadingIcon If it has a leading icon.
	 * @param options.hasTrailingIcon If it has a trailing icon.
	 * @param options.theme Theme.
	 * @param options.hasValue ...
	 * @param options.hasFocus ...
	 * @returns Css rule.
	 */
	public static getLabelTransform(options: IStyleOptions): string {
		const { spacing } = options.theme;
		if ((options.hasValue || options.hasFocus) && options.hasLabel) {
			return `translateY(-${56 / 16 / 2}rem)`;
		} else if (options.hasLeadingIcon && options.hasLabel) {
			return `translateX(${spacing.space150 / 16}rem)`;
		}
		return 'none';
	}

	/**
	 *
	 * @param options ...
	 */
	public static getRtlLabel(options: IStyleOptions): string {
		const { spacing } = options.theme;
		if (options.direction === 'rtl') {
			if ((options.hasValue || options.hasFocus) && options.hasLabel) {
				return css`
					label {
						transform: translateY(-${56 / 16 / 2}rem);
					}
				`;
			} else if (options.hasLeadingIcon && options.hasLabel) {
				return css`
					label {
						transform: translateX(-${spacing.space150 / 16}rem);
					}
				`;
			}
		}
		if ((options.hasValue || options.hasFocus) && options.hasLabel) {
			return css`
				:host(:dir(rtl)) label {
					transform: translateY(-${56 / 16 / 2}rem);
				}
			`;
		} else if (options.hasLeadingIcon && options.hasLabel) {
			return css`
				:host-context([dir='rtl']) label {
					transform: translateX(-${spacing.space150 / 16}rem);
				}
			`;
		}
		return '';
	}

	/**
	 * @param options Options holder.
	 * @returns Font size.
	 */
	public static getLabelFontStyle(options: IStyleOptions): string {
		if (options.hasValue || options.hasFocus) {
			return FontStyle.getStyle({
				type: ThemeFontStyleTypeEnum.bodySmall,
				theme: options.theme,
				querySelector: 'label'
			});
		}
		return FontStyle.getStyle({
			type: ThemeFontStyleTypeEnum.bodyLarge,
			theme: options.theme,
			querySelector: 'label'
		});
	}

	/**
	 * Get help message color.
	 *
	 * @param options Options.
	 * @returns Message color.
	 */
	public static getHelpMessageColor(options: IStyleOptions): string {
		const { color } = options.theme;
		if (options.disabled) {
			return color.disabled.default;
		}

		switch (options.layout) {
			case InputLayoutEnum.success:
				return color.semantic.positive;
			case InputLayoutEnum.error:
				return color.semantic.negative;
			default:
				return color.text.lightest;
		}
	}

	/**
	 * Get box shadow color.
	 *
	 * @param options Options.
	 */
	public static getBoxShadow(options: IStyleOptions): string {
		if (options.type === InputTypeEnum.subtle && options.hasFocus) {
			return `0 ${2 / 16}rem 0 0 ${this.getBorderColor(options)}`;
		} else if (
			(!options.hasFocus && options.layout === InputLayoutEnum.default) ||
			options.disabled ||
			options.layout === InputLayoutEnum.readOnly
		) {
			return `0 0 0 0 transparent`;
		}
		return `0 ${1 / 16}rem 0 0 ${this.getBorderColor(options)}`;
	}

	/**
	 * @param options Options holder.
	 * @param options.theme Theme.
	 * @param options.hasFocus If it has focus.
	 * @returns Color.
	 */
	public static getBorderColor(options: IStyleOptions): string {
		const { color } = options.theme;
		if (options.disabled) {
			return color.disabled.default;
		}
		if (!options.hasFocus && options.layout === InputLayoutEnum.default) {
			return color.border.default;
		}

		switch (options.layout) {
			case InputLayoutEnum.error: {
				return color.semantic.negative;
			}
			case InputLayoutEnum.success: {
				return color.semantic.positive;
			}
			case InputLayoutEnum.readOnly: {
				return color.disabled.default;
			}
			default: {
				return color.accent.primaryAccent;
			}
		}
	}
}
